/**
 * UI Store
 *
 * Manages UI state including:
 * - Modal state
 * - Toast notifications
 * - Sidebar state
 * - Loading states
 *
 * @module @schoolidb/store/uiStore
 */

import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

/**
 * Toast types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification
 */
export interface Toast {
  /** Unique ID */
  id: string;
  /** Toast type */
  type: ToastType;
  /** Toast message */
  message: string;
  /** Optional title */
  title?: string;
  /** Duration in ms (0 = persistent) */
  duration: number;
  /** Timestamp */
  createdAt: number;
}

/**
 * Modal configuration
 */
export interface ModalConfig {
  /** Modal ID */
  id: string;
  /** Modal component name */
  component: string;
  /** Modal props */
  props?: Record<string, unknown>;
  /** Whether modal can be closed by clicking outside */
  closeOnOverlay?: boolean;
  /** Whether modal can be closed with escape key */
  closeOnEscape?: boolean;
}

/**
 * UI state
 */
export interface UIState {
  /** Active modals stack */
  modals: ModalConfig[];
  /** Active toasts */
  toasts: Toast[];
  /** Sidebar open state */
  sidebarOpen: boolean;
  /** Mobile menu open state */
  mobileMenuOpen: boolean;
  /** Global loading state */
  isLoading: boolean;
  /** Loading message */
  loadingMessage: string | null;
}

/**
 * UI actions
 */
export interface UIActions {
  /** Open a modal */
  openModal: (config: Omit<ModalConfig, 'id'> & { id?: string }) => string;
  /** Close a modal by ID */
  closeModal: (id?: string) => void;
  /** Close all modals */
  closeAllModals: () => void;
  /** Show a toast */
  showToast: (toast: Omit<Toast, 'id' | 'createdAt'>) => string;
  /** Remove a toast */
  removeToast: (id: string) => void;
  /** Clear all toasts */
  clearToasts: () => void;
  /** Toggle sidebar */
  toggleSidebar: () => void;
  /** Set sidebar state */
  setSidebarOpen: (open: boolean) => void;
  /** Toggle mobile menu */
  toggleMobileMenu: () => void;
  /** Set mobile menu state */
  setMobileMenuOpen: (open: boolean) => void;
  /** Set loading state */
  setLoading: (loading: boolean, message?: string) => void;
}

export type UIStore = UIState & UIActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: UIState = {
  modals: [],
  toasts: [],
  sidebarOpen: true,
  mobileMenuOpen: false,
  isLoading: false,
  loadingMessage: null,
};

// ============================================================================
// Helpers
// ============================================================================

let toastIdCounter = 0;
let modalIdCounter = 0;

function generateToastId(): string {
  return `toast-${++toastIdCounter}-${Date.now()}`;
}

function generateModalId(): string {
  return `modal-${++modalIdCounter}-${Date.now()}`;
}

// ============================================================================
// Store
// ============================================================================

/**
 * UI store (no persistence - ephemeral state)
 */
export const useUIStore = create<UIStore>()((set, get) => ({
  ...initialState,

  openModal: (config) => {
    const id = config.id || generateModalId();
    const modalConfig: ModalConfig = {
      ...config,
      id,
      closeOnOverlay: config.closeOnOverlay ?? true,
      closeOnEscape: config.closeOnEscape ?? true,
    };
    
    set((state) => ({
      modals: [...state.modals, modalConfig],
    }));
    
    return id;
  },

  closeModal: (id) => {
    set((state) => {
      if (id) {
        return { modals: state.modals.filter((m) => m.id !== id) };
      }
      // Close the topmost modal
      return { modals: state.modals.slice(0, -1) };
    });
  },

  closeAllModals: () => {
    set({ modals: [] });
  },

  showToast: (toast) => {
    const id = generateToastId();
    const newToast: Toast = {
      ...toast,
      id,
      createdAt: Date.now(),
      duration: toast.duration ?? 5000,
    };
    
    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));
    
    // Auto-remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, newToast.duration);
    }
    
    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },

  toggleSidebar: () => {
    set((state) => ({ sidebarOpen: !state.sidebarOpen }));
  },

  setSidebarOpen: (sidebarOpen) => {
    set({ sidebarOpen });
  },

  toggleMobileMenu: () => {
    set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }));
  },

  setMobileMenuOpen: (mobileMenuOpen) => {
    set({ mobileMenuOpen });
  },

  setLoading: (isLoading, loadingMessage = null) => {
    set({ isLoading, loadingMessage });
  },
}));

// ============================================================================
// Selectors
// ============================================================================

export const selectModals = (state: UIStore) => state.modals;
export const selectTopModal = (state: UIStore) => state.modals[state.modals.length - 1];
export const selectToasts = (state: UIStore) => state.toasts;
export const selectSidebarOpen = (state: UIStore) => state.sidebarOpen;
export const selectMobileMenuOpen = (state: UIStore) => state.mobileMenuOpen;
export const selectIsLoading = (state: UIStore) => state.isLoading;
export const selectLoadingMessage = (state: UIStore) => state.loadingMessage;

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Show success toast
 */
export const showSuccess = (message: string, title?: string) =>
  useUIStore.getState().showToast({ type: 'success', message, title, duration: 5000 });

/**
 * Show error toast
 */
export const showError = (message: string, title?: string) =>
  useUIStore.getState().showToast({ type: 'error', message, title, duration: 7000 });

/**
 * Show warning toast
 */
export const showWarning = (message: string, title?: string) =>
  useUIStore.getState().showToast({ type: 'warning', message, title, duration: 6000 });

/**
 * Show info toast
 */
export const showInfo = (message: string, title?: string) =>
  useUIStore.getState().showToast({ type: 'info', message, title, duration: 5000 });
